import { Injectable } from '@angular/core';
import { HubConnection, HubConnectionBuilder, RetryContext } from '@microsoft/signalr';
import { environment } from 'environments/environment';
import { Observable, Subject, defer, shareReplay, switchMap } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { mapJsonObjectToClient } from '../services';

export type ObserverAction = (observer: { next: () => void }) => void;
export type ObserverAction1<TParam> = (observer: { next: (arg: TParam) => void }) => void;
export type ObserverAction2<TParam1, TParam2> = (observer: { next: (args: [TParam1, TParam2]) => void }) => void;
export type ObserverAction3<TParam1, TParam2, TParam3> = (observer: {
  next: (args: [TParam1, TParam2, TParam3]) => void;
}) => void;
export type ObserverAction4<TParam1, TParam2, TParam3, TParam4> = (observer: {
  next: (args: [TParam1, TParam2, TParam3, TParam4]) => void;
}) => void;
export type ObserverAction5<TParam1, TParam2, TParam3, TParam4, TParam5> = (observer: {
  next: (args: [TParam1, TParam2, TParam3, TParam4, TParam5]) => void;
}) => void;

export interface NotificationSink {
  on$(methodName: string, onreconnected?: ObserverAction): Observable<void>;
  on$<TParam>(methodName: string, onreconnected?: ObserverAction1<TParam>): Observable<TParam>;
  on$<TParam1, TParam2>(
    methodName: string,
    onreconnected?: ObserverAction2<TParam1, TParam2>
  ): Observable<[TParam1, TParam2]>;
  on$<TParam1, TParam2, TParam3>(
    methodName: string,
    onreconnected?: ObserverAction3<TParam1, TParam2, TParam3>
  ): Observable<[TParam1, TParam2, TParam3]>;
  on$<TParam1, TParam2, TParam3, TParam4>(
    methodName: string,
    onreconnected?: ObserverAction4<TParam1, TParam2, TParam3, TParam4>
  ): Observable<[TParam1, TParam2, TParam3, TParam4]>;
  on$<TParam1, TParam2, TParam3, TParam4, TParam5>(
    methodName: string,
    onreconnected?: ObserverAction5<TParam1, TParam2, TParam3, TParam4, TParam5>
  ): Observable<[TParam1, TParam2, TParam3, TParam4, TParam5]>;
}

interface ConnectionContext {
  connection: HubConnection;
  onreconnected$: Observable<void>;
}

const hubConnectionPool: Record<string, Observable<ConnectionContext>> = {};

class InternalNotificationSink {
  constructor(private url: string, private authService: AuthService) {}

  on$(methodName: string, onreconnected?: ObserverAction): Observable<void>;
  on$<TParam>(methodName: string, onreconnected?: ObserverAction1<TParam>): Observable<TParam>;
  on$<TParam1, TParam2>(
    methodName: string,
    onreconnected?: ObserverAction2<TParam1, TParam2>
  ): Observable<[TParam1, TParam2]>;
  on$<TParam1, TParam2, TParam3>(
    methodName: string,
    onreconnected?: ObserverAction3<TParam1, TParam2, TParam3>
  ): Observable<[TParam1, TParam2, TParam3]>;
  on$<TParam1, TParam2, TParam3, TParam4>(
    methodName: string,
    onreconnected?: ObserverAction4<TParam1, TParam2, TParam3, TParam4>
  ): Observable<[TParam1, TParam2, TParam3, TParam4]>;
  on$<TParam1, TParam2, TParam3, TParam4, TParam5>(
    methodName: string,
    onreconnected?: ObserverAction5<TParam1, TParam2, TParam3, TParam4, TParam5>
  ): Observable<[TParam1, TParam2, TParam3, TParam4, TParam5]>;
  on$(methodName: string, onreconnected?: ObserverAction): Observable<any | any[]> {
    return this.getConnection().pipe(
      switchMap(
        (context) =>
          new Observable<any | any[]>((observer) => {
            const connection = context?.connection;
            const method = (...args: any[]): void => {
              const [firstArg, ...remainingArgs] = args;
              observer.next(mapJsonObjectToClient(remainingArgs.length ? args : firstArg));
            };

            connection?.on(methodName, method);

            const reconnectSub =
              onreconnected &&
              context &&
              context.onreconnected$.subscribe(() => {
                onreconnected({
                  next: (...args: any[]) => observer.next(args),
                });
              });

            return () => {
              reconnectSub?.unsubscribe();
              connection?.off(methodName, method);
              observer.complete();
            };
          })
      )
    );
  }

  private getConnection(): Observable<ConnectionContext> {
    const normalizedUrl = this.url.toLowerCase();
    const connection = hubConnectionPool[normalizedUrl];
    if (connection) {
      return connection;
    }

    return (hubConnectionPool[normalizedUrl] = defer(
      () =>
        new Observable<ConnectionContext>((observer) => {
          // Create a new connection whenever the access token changes
          let context: ConnectionContext;
          const authSub = this.authService.accessToken$.subscribe((accessToken) => {
            // Stop and discard current connection
            context?.connection.stop().catch(() => {
              /* Ignore errors */
            });
            context = null;
            if (accessToken) {
              context = this.createConnection(accessToken);

              console.log(`Starting hub connection '${this.url}'`);
              context.connection
                .start()
                .then(() => observer.next(context))
                .catch((err) => {
                  delete hubConnectionPool[normalizedUrl];
                  observer.error(err);
                });
            }
          });

          return () => {
            console.log(`Stopping hub connection '${this.url}'`);
            delete hubConnectionPool[normalizedUrl];
            authSub.unsubscribe();
            context?.connection.stop().catch(() => {
              /* Ignore errors */
            });
            observer.complete();
          };
        })
    ).pipe(shareReplay({ bufferSize: 1, refCount: true })));
  }

  private createConnection(accessToken: string): ConnectionContext {
    const url = `${environment.apiRoot}/${this.url}`;
    const connection = new HubConnectionBuilder()
      .withUrl(url, {
        accessTokenFactory: () => accessToken,
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (retryContext: RetryContext): number => {
          // Wait 0, 2, 10 and then 30 seconds for all subsequent attempts
          const retryDelays = [0, 2, 10];
          let retryDelay = retryDelays[retryContext.previousRetryCount];
          retryDelay = retryDelay == null ? 30 : retryDelay;
          console.log(`Retry hub connection '${this.url}' in ${retryDelay} seconds...`);

          if (retryDelay === 30) {
            // Check if we are still authenticated
            this.authService.check().subscribe();
          }

          return retryDelay * 1000;
        },
      })
      .build();

    // Reconnection handler
    const onreconnected$ = new Subject<void>();
    connection.onreconnected((connectionId) => {
      if (connection.connectionId === connectionId) {
        onreconnected$.next();
      }
    });

    return {
      connection,
      onreconnected$,
    };
  }
}

@Injectable({ providedIn: 'root' })
export class NotificationSinkService {
  constructor(private authService: AuthService) {}

  create(url: string): NotificationSink {
    return new InternalNotificationSink(url, this.authService);
  }
}
